import * as Sentry from '@sentry/browser';
import { useQueryClient } from '@tanstack/react-query';
import { MailSearch, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Button } from '@/components/shadcn/button';
import useCrm from '@/features/crm/hooks/useCrm';
import type { CRM } from '@/features/crm/types';
import useBroker from '@/hooks/useBroker';
import hasItems from '@/lib/utils/hasItems';
import queryKeys from '@/queries/queryKeys';

import useCreateLegalParty from '../../mutations/useCreateLegalParty';
import useCreateNaturalParty from '../../mutations/useCreateNaturalParty';

import CreatePartyForm from './CreatePartyForm';
import toPartyAddress from './toPartyAddress';
import type { CreatePartyValues } from './types';

export default function CreatePartyModal({ onClose }: ModalProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const {
    party: { extractedParty, isExtractingParty },
    linkParty,
  } = useEmail();

  const { mutateAsync: createNaturalParty } = useCreateNaturalParty();
  const { mutateAsync: createLegalParty } = useCreateLegalParty();

  const { exportParty } = useCrm();
  const { crms: distributionCrms } = useBroker();

  const formatCrmNames = (crms: Array<CRM>) => {
    const names = crms.map(
      (crm) => distributionCrms?.find(({ key }) => key === crm)?.name ?? crm,
    );

    return new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
      names,
    );
  };

  const createParty = (values: CreatePartyValues) => {
    const address = toPartyAddress(values.address);

    if (values.entityType === 'NATURAL') {
      return createNaturalParty({
        ...extractedParty,
        email: values.email,
        address,
        first_name: values.firstName,
        last_name: values.lastName,
        initials: values.initials,
        birth: values.birthdate || undefined,
      });
    }

    return createLegalParty({
      ...extractedParty,
      email: values.email,
      address,
      name: values.companyName ?? '',
    });
  };

  const onSubmit = async (values: CreatePartyValues) => {
    const selectedCrms = Object.entries(values.exportToCrm)
      .filter(([, shouldExport]) => shouldExport)
      .map(([crm]) => crm as CRM);

    let partyId: string;

    try {
      const party = await createParty(values);
      partyId = party.id;
    } catch (error) {
      toast.error(t('PARTY_CREATE_INLINE_ERROR_GENERIC'));
      Sentry.captureException(error);

      return;
    }

    linkParty(partyId);
    onClose();

    if (!hasItems(selectedCrms)) {
      toast.success(t('PARTY_CREATE_INLINE_SUCCESS_TITLE'));

      return;
    }

    const toastId = toast.loading(
      t('PARTY_CREATE_INLINE_EXPORTING', {
        crms: formatCrmNames(selectedCrms),
      }),
    );

    const { failedCrms, errorMessageByCrm } = await exportParty(
      partyId,
      selectedCrms,
    );

    queryClient.invalidateQueries({
      queryKey: queryKeys.party.byId(partyId),
    });

    if (!hasItems(failedCrms)) {
      toast.success(t('PARTY_CREATE_INLINE_EXPORTED'), { id: toastId });

      return;
    }

    const exportedCrms = selectedCrms.filter(
      (crm) => !failedCrms.includes(crm),
    );

    const successTitle = hasItems(exportedCrms)
      ? t('PARTY_CREATE_INLINE_EXPORTED_TO', {
          crms: formatCrmNames(exportedCrms),
        })
      : t('PARTY_CREATE_INLINE_SUCCESS_TITLE');

    toast.success(successTitle, { id: toastId });

    failedCrms.forEach((crm) =>
      toast.error(
        t('PARTY_CREATE_INLINE_EXPORT_FAILED', { crms: formatCrmNames([crm]) }),
        {
          description: errorMessageByCrm[crm],
          duration: Infinity,
          closeButton: true,
        },
      ),
    );
  };

  if (isExtractingParty || !extractedParty)
    return (
      <div className="relative flex min-w-full flex-col gap-4 p-4">
        <Button
          onClick={onClose}
          className="absolute top-2 right-2"
          variant="ghost"
          size="icon-xs"
        >
          <X />
        </Button>

        <p className="text-sm font-bold">{t('PARTY_CREATE_INLINE_TITLE')}</p>

        <div className="flex flex-col items-center justify-center gap-4 py-4">
          <MailSearch className="text-primary-500 size-8 animate-pulse" />

          <div className="flex flex-col items-center text-center">
            <p className="text-foreground mb-1 text-sm">
              {t('PARTY_EXTRACTING_INFO')}
            </p>
            <p className="text-muted-foreground text-xs">
              {t('PARTY_EXTRACTING_INFO_DESC')}
            </p>
          </div>
        </div>
      </div>
    );

  return (
    <CreatePartyForm
      onClose={onClose}
      extractedParty={extractedParty}
      onSubmit={onSubmit}
    />
  );
}
